import React, { useCallback } from "react";
import { CellData } from "./table";
import { TableCell } from "./tableCell/tableCell";

interface TableBodyProps {
  computedCellData: CellData[][];
  tableDataToRender: any[];
  tableWidth: number;
  onRowClick: (
    tableData: any[],
    rowIndex: number,
    className: string,
    cellHeader?: string,
    cellType?: string,
  ) => void;
  onStatusClick?: (data: any) => void;
  onDeleteIconClick?: (data: any) => void;
  onEditIconClick?: (data: any) => void;
  onClickableTextWithDisabledStatusClick?: (data: any) => void;
  onComplete?: () => void;
  toggleCheckboxSelection?: (id: string) => void;
  onDownloadIconClick?: (filePath: string, fileName?: string) => void;
  startIndex?: number;
  expandableRowContent?: (rowData: any, rowIndex: number) => React.ReactNode;
  onRowExpansionChangeFromTableBody?: (expanded: boolean, id: string) => void;
  sortedTableHeaders?: any[];
  isExpandableRowTable?: boolean;
  onButtonWithThreeDotsOptionClick?: (optionId: string, rowId: string) => void;
  scrollableWidth?: number;
  onActionOnInputClick?: (data: string, id: string, fieldId?: string) => void;
  onInputChange?: (data: string, id: string, fieldId?: string) => void;
  onTagOptionClick?: (
    menuId: string,
    dataId?: string,
    fieldId?: string,
  ) => void;
  onTagDropDownSelectChange?: (
    value: string,
    dataId?: string,
    fieldId?: string,
  ) => void;
  onCellValueChange?: (rowData: any, fieldKey: string, newValue: any) => void;
  onChangeEmit?: (data: {
    name: string;
    required: boolean;
    value: boolean;
  }) => void;
}

const TableBody: React.FC<TableBodyProps> = ({
  computedCellData,
  tableDataToRender,
  tableWidth,
  onRowClick,
  onStatusClick,
  onDeleteIconClick,
  onEditIconClick,
  onClickableTextWithDisabledStatusClick,
  onComplete,
  toggleCheckboxSelection,
  onDownloadIconClick,
  startIndex,
  expandableRowContent,
  onRowExpansionChangeFromTableBody,
  sortedTableHeaders,
  isExpandableRowTable,
  onButtonWithThreeDotsOptionClick,
  scrollableWidth,
  onActionOnInputClick,
  onInputChange,
  onTagOptionClick,
  onTagDropDownSelectChange,
  onCellValueChange,
  onChangeEmit,
}) => {
  const isRowExpanded = useCallback(
    (rowIndex: number): boolean => {
      const actualRowIndex = rowIndex + (startIndex ?? 0);
      const rowData = tableDataToRender[actualRowIndex];
      return rowData?.expandableRowIcon?.isExpanded ?? false;
    },
    [tableDataToRender, startIndex],
  );

  const onRowExpansionChange = (
    expanded: boolean,
    id: string,
    rowIndex: number,
  ) => {
    onRowExpansionChangeFromTableBody?.(expanded, id);
  };

  const columnCount =
    computedCellData[0]?.length || sortedTableHeaders?.length || 1;

  return (
    <tbody>
      {!isExpandableRowTable ? (
        <>
          {computedCellData.map((rowData, rowIndex) => {
            // Row-level left border (full height): set `rowLeftAccentColor` / `rowLeftAccentTitle` on table row data.
            const rowMeta = rowData[0]?.rowData as
              | { rowLeftAccentColor?: string; rowLeftAccentTitle?: string }
              | undefined;
            const rowLeftAccentColor = rowMeta?.rowLeftAccentColor;
            const rowLeftAccentTitle = rowMeta?.rowLeftAccentTitle?.trim();

            return (
              <tr
                key={rowIndex}
                title={rowLeftAccentTitle || undefined}
                style={{
                  display: "flex",
                  width: scrollableWidth ? `${scrollableWidth}%` : "100%",
                  alignItems: "stretch",
                  borderLeft: rowLeftAccentColor
                    ? `4px solid ${rowLeftAccentColor}`
                    : undefined,
                  boxSizing: "border-box",
                }}
              >
                {rowData.map((cell, cellIndex) => (
                  <td
                    key={cellIndex}
                    className={`table-cell ${cell.type}__${cell.header}`}
                    style={{
                      width: `${cell?.width}%`,
                    }}
                    onClick={() =>
                      onRowClick(
                        tableDataToRender,
                        rowIndex,
                        `table-cell ${cell.type}__${cell.header}`,
                        cell.header,
                        cell.type,
                      )
                    }
                  >
                    <TableCell
                      type={cell.type}
                      id={cell.rowData?.id}
                      value={cell.value}
                      header={cell.header}
                      rowIndex={rowIndex}
                      globalRowIndex={
                        startIndex ? startIndex + rowIndex : rowIndex
                      }
                      rowData={cell.rowData}
                      onReviewClick={
                        cell.type === "badge-button"
                          ? () =>
                              onRowClick(
                                tableDataToRender,
                                rowIndex,
                                `table-cell ${cell.type}__${cell.header}`,
                                cell.header,
                                cell.type,
                              )
                          : undefined
                      }
                      handleStatusClick={onStatusClick}
                      handleDeleteIconClick={onDeleteIconClick}
                      handleEditIconClick={onEditIconClick}
                      handleClickableTextWithDisabledStatusClick={
                        onClickableTextWithDisabledStatusClick
                      }
                      onComplete={onComplete}
                      toggleCheckboxSelection={toggleCheckboxSelection}
                      onDownloadIconClick={onDownloadIconClick}
                      onButtonWithThreeDotsOptionClick={
                        onButtonWithThreeDotsOptionClick
                      }
                      onActionOnInputClick={onActionOnInputClick}
                      onInputChange={onInputChange}
                      onTagOptionClick={onTagOptionClick}
                      onTagDropDownSelectChange={onTagDropDownSelectChange}
                      onCellValueChange={onCellValueChange}
                      onChangeEmit={onChangeEmit}
                    />
                  </td>
                ))}
              </tr>
            );
          })}
        </>
      ) : (
        <>
          {computedCellData.map((rowData, rowIndex) => (
            <React.Fragment key={rowIndex}>
              {/* Main row with table cells */}
              <tr
                style={{ display: "block", width: "100%" }}
                className={isRowExpanded(rowIndex) ? "expanded-row" : ""}
              >
                <td
                  colSpan={columnCount}
                  style={{
                    padding: 0,
                    border: "none",
                    display: "block",
                    width: "100%",
                  }}
                >
                  <div
                    style={{ display: "flex", width: "100%" }}
                    className={`${isRowExpanded(rowIndex) ? "single-row--container shadow" : "single-row--container"}`}
                  >
                    {rowData.map((cell, cellIndex) => (
                      <div
                        key={cellIndex}
                        className={`table-cell ${cell.type}__${cell.header} ${isRowExpanded(rowIndex) ? "expanded" : ""}`}
                        style={{
                          width: `${cell?.width}%`,
                          padding: "0.75rem 0.75rem",
                          textAlign: "left",
                          borderBottom: `${rowIndex === computedCellData.length - 1 ? "none" : "1px solid #e5e7eb"}`,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        onClick={() =>
                          onRowClick(
                            tableDataToRender,
                            rowIndex,
                            `table-cell ${cell.type}__${cell.header}`,
                            cell.header,
                            cell.type,
                          )
                        }
                      >
                        <TableCell
                          type={cell.type}
                          id={cell.rowData?.id}
                          value={cell.value}
                          header={cell.header}
                          rowIndex={rowIndex}
                          globalRowIndex={
                            startIndex ? startIndex + rowIndex : rowIndex
                          }
                          rowData={cell.rowData}
                          onReviewClick={
                            cell.type === "badge-button"
                              ? () =>
                                  onRowClick(
                                    tableDataToRender,
                                    rowIndex,
                                    `table-cell ${cell.type}__${cell.header}`,
                                    cell.header,
                                    cell.type,
                                  )
                              : undefined
                          }
                          handleStatusClick={onStatusClick}
                          handleDeleteIconClick={onDeleteIconClick}
                          handleEditIconClick={onEditIconClick}
                          handleClickableTextWithDisabledStatusClick={
                            onClickableTextWithDisabledStatusClick
                          }
                          onComplete={onComplete}
                          toggleCheckboxSelection={toggleCheckboxSelection}
                          onDownloadIconClick={onDownloadIconClick}
                          onRowExpandChange={(expanded, id) => {
                            onRowExpansionChange(expanded, id, rowIndex);
                          }}
                          onButtonWithThreeDotsOptionClick={
                            onButtonWithThreeDotsOptionClick
                          }
                          onActionOnInputClick={onActionOnInputClick}
                          onInputChange={onInputChange}
                          onTagOptionClick={onTagOptionClick}
                          onTagDropDownSelectChange={onTagDropDownSelectChange}
                          onCellValueChange={onCellValueChange}
                          onChangeEmit={onChangeEmit}
                        />
                      </div>
                    ))}
                  </div>
                </td>
              </tr>

              {/* Expandable content row */}
              {expandableRowContent && isRowExpanded(rowIndex) && (
                <tr
                  style={{
                    display: "block",
                    width: "100%",
                    borderLeft: `4px solid ${tableDataToRender[rowIndex + (startIndex ?? 0)]?.expandableRowIcon?.borderColor}`,
                  }}
                >
                  <td
                    colSpan={columnCount}
                    style={{
                      padding: 0,
                      border: "none",
                      display: "block",
                      width: "100%",
                    }}
                  >
                    <div
                      style={{
                        width: "100%",
                        overflow: "auto",
                        padding: "1rem 1rem",
                        borderBottom: `${rowIndex === computedCellData.length - 1 ? "none" : "1px solid #e5e7eb"}`,
                        marginTop: "2px",
                        marginBottom: "2px",
                      }}
                      className={`${isRowExpanded(rowIndex) ? "slide-down" : "slide-up"}`}
                    >
                      {typeof expandableRowContent === "function" &&
                        expandableRowContent(
                          tableDataToRender[rowIndex + (startIndex ?? 0)],
                          rowIndex + (startIndex ?? 0),
                        )}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </>
      )}
    </tbody>
  );
};

export default TableBody;
